import React from 'react'
import { FasDataItem, FasDataItemPlanningStatus, StatusIndicators } from '../types'
import dayjs from 'dayjs'
import 'dayjs/locale/da'

dayjs.locale('da')

// MUI Components
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import SvgIcon from '@mui/material/SvgIcon'
import Expand_down from '@tdcerhverv/parrotfish/dist/icons/Icons/Expand_down.svg'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Error from '@tdcerhverv/parrotfish/dist/icons/Icons/Error.svg'
import Warning from '@tdcerhverv/parrotfish/dist/icons/Icons/Warning.svg'
import Success from '@tdcerhverv/parrotfish/dist/icons/Icons/Success.svg'
import { useTheme } from '@mui/material'

const AccordionComponent = (props: FasDataItem) => {
  const {
    fasId,
    title,
    geography,
    occured,
    plannedStart,
    updated,
    estimateResolved,
    nextStatus,
    reason,
    consequence,
    status,
    planningStatus,
  } = props

  const { palette, breakpoints, typography } = useTheme()
  const styles = {
    summery: {
      border: `1px solid ${palette.grey[300]}`,
      borderRadius: '10px',
      '& .MuiAccordionSummary-content': {
        display: 'flex',
        alignItems: 'center',
      },
    },
    expandIcon: {
      fontSize: '14px',
    },
    summaryTitles: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px',
      color: palette.common.black,
      ...typography.subtitle1,

      [breakpoints.down('lg')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    AccordionDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      paddingTop: '15px',
    },
    statusTable: {
      padding: 0,
      margin: 0,
      '& li': {
        display: 'flex',
        gap: '16px',
        '& > span': {
          flex: '1 0 0',
        },
      },
    },
    consequence: {
      whiteSpace: 'pre-line',
    },
  }
  const [expanded, setExpanded] = React.useState<string | false>(false)
  const statusIndicators: StatusIndicators = {
    [FasDataItemPlanningStatus.Unplanned]: {
      icon: Error,
      color: 'error',
    },
    [FasDataItemPlanningStatus.Planned]: {
      icon: Warning,
      color: 'secondary',
    },
    [FasDataItemPlanningStatus.Completed]: {
      icon: Success,
      color: 'inherit',
    },
  }

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }
  return (
    <Box>
      <Accordion id={fasId} key={fasId} expanded={expanded === 'panel'} onChange={handleChange('panel')}>
        <AccordionSummary
          sx={styles.summery}
          expandIcon={<SvgIcon component={Expand_down} sx={styles.expandIcon} viewBox="0 0 48 48" />}
          aria-controls="panel-content"
          id="panel-header"
        >
          <SvgIcon
            color={statusIndicators[planningStatus].color}
            component={statusIndicators[planningStatus].icon}
            viewBox="0 0 48 48"
            {...(FasDataItemPlanningStatus.Completed === planningStatus
              ? { style: { color: palette.success.main } }
              : {})}
          />
          <Box sx={styles.summaryTitles}>
            <span>{title}</span>
            <span style={{ paddingRight: '5px' }}>{dayjs(plannedStart || occured).format('DD.MM.YYYY')}</span>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={styles.AccordionDetails}>
          <Typography variant="subtitle2">Område: {geography}</Typography>
          <Box component="ul" sx={styles.statusTable}>
            <li>
              <span>Opstået:</span>
              <span>{occured ? dayjs(occured).format('DD.MM.YYYY HH.mm') : '(tidspunkt tilkommer)'}</span>
            </li>
            <li>
              <span>Opdateret:</span>
              <span>{updated ? dayjs(updated).format('DD.MM.YYYY HH.mm') : '(tidspunkt tilkommer)'}</span>
            </li>
            <li>
              <span>Næste opdatering:</span>
              <span>{nextStatus ? dayjs(nextStatus).format('DD.MM.YYYY HH.mm') : '(tidspunkt tilkommer)'}</span>
            </li>
            <li>
              <span>Forventes løst:</span>
              <span>
                {estimateResolved ? dayjs(estimateResolved).format('DD.MM.YYYY HH.mm') : '(tidspunkt tilkommer)'}
              </span>
            </li>
            <li>
              <span>Id:</span>
              <span>{fasId}</span>
            </li>
          </Box>
          {reason && (
            <div>
              <Typography variant="subtitle2">Årsag:</Typography>
              <Typography variant="body2">{reason}</Typography>
            </div>
          )}
          {consequence && (
            <Typography sx={styles.consequence} variant="body2">
              {consequence}
            </Typography>
          )}
          {status.trim() !== '' && (
            <div>
              <Typography variant="subtitle2">Status:</Typography>
              <Typography variant="body2">{status}</Typography>
            </div>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default AccordionComponent
